import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DialogFooter } from "@/components/ui/dialog";
import { ConversionPreview } from "@/components/conversion-preview/ConversionPreview";
import { Account } from "@accountHooks";
import { useCurrencyList } from "@/hooks/useCurrencyList";
import { useExchangeRates } from "@/hooks/useExchangeRates";
import { getDisplayName, disambiguateDisplayNames } from "@/lib/display-name";
import {ReitHolding} from "@/@types/reit.models.ts";
import {RecordREITDividendDto} from "@/@types/reit.dto";

interface REITDividendFormProps {
  holdings: ReitHolding[];
  accounts: Account[];
  preselectedHoldingId?: string;
  onSave: (data: RecordREITDividendDto) => void | Promise<void>;
  onCancel: () => void;
}

export default function REITDividendForm({
  holdings,
  accounts,
  preselectedHoldingId,
  onSave,
  onCancel,
}: REITDividendFormProps) {
  const [holdingId, setHoldingId] = useState(preselectedHoldingId ?? "");
  const [paymentDate, setPaymentDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [amountPerUnit, setAmountPerUnit] = useState("");
  const [unitsAtPayment, setUnitsAtPayment] = useState("");
  const [currency, setCurrency] = useState("USD");
  const [notes, setNotes] = useState("");
  const [dividendAction, setDividendAction] = useState<
    "reinvest" | "deposit_to_account"
  >("deposit_to_account");
  const [targetAccountId, setTargetAccountId] = useState("");
  const [reinvestedUnits, setReinvestedUnits] = useState("");

  const {
    data: availableCurrencies,
    isLoading: isLoadingCurrencies,
    error: errorLoadingCurrencies,
  } = useCurrencyList();
  const { getRate, isLoading: isLoadingRates } = useExchangeRates();

  // Auto-fill units and currency when holding is selected
  const handleHoldingChange = (id: string) => {
    setHoldingId(id);
    const h = holdings.find((h) => h.id === id);
    if (h) {
      setUnitsAtPayment(h.units_held.toString());
      setCurrency(h.currency);
    }
  };

  const selectedHolding = holdings.find((h) => h.id === holdingId);
  const totalAmount =
    amountPerUnit && unitsAtPayment
      ? (parseFloat(amountPerUnit) * parseFloat(unitsAtPayment)).toFixed(2)
      : null;

  const selectedTargetAccount = accounts.find((a) => a.id === targetAccountId);
  const targetAccountCurrency = selectedTargetAccount?.currency ?? "";

  const showConversionPreview =
    dividendAction === "deposit_to_account" &&
    !!currency &&
    !!targetAccountCurrency &&
    currency !== targetAccountCurrency &&
    !!totalAmount &&
    parseFloat(totalAmount) > 0;

  const disambiguatedAccounts = disambiguateDisplayNames(
    accounts.map((a) => ({ ...a, displayName: getDisplayName(a) }))
  );

  const isSubmitDisabled =
    !holdingId ||
    !amountPerUnit ||
    !unitsAtPayment ||
    (dividendAction === "deposit_to_account" && !targetAccountId) ||
    (dividendAction === "reinvest" &&
      (!reinvestedUnits || parseFloat(reinvestedUnits) <= 0));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitDisabled) return;
    onSave({
      reit_holding_id: holdingId,
      payment_date: paymentDate,
      amount_per_unit: parseFloat(amountPerUnit),
      units_held_at_payment: parseFloat(unitsAtPayment),
      currency,
      notes: notes.trim() || undefined,
      dividend_action: dividendAction,
      target_account_id:
        dividendAction === "deposit_to_account"
          ? targetAccountId || undefined
          : undefined,
      reinvested_units:
        dividendAction === "reinvest"
          ? parseFloat(reinvestedUnits) || undefined
          : undefined,
    });
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="space-y-4"
      aria-label="Record dividend payment"
    >
      {/* REIT Holding */}
      <div className="space-y-2">
        <Label htmlFor="dividend-holding">REIT Holding *</Label>
        <Select value={holdingId} onValueChange={handleHoldingChange}>
          <SelectTrigger id="dividend-holding" aria-label="Select REIT holding">
            <SelectValue placeholder="Select a REIT holding" />
          </SelectTrigger>
          <SelectContent>
            {holdings.map((h) => (
              <SelectItem key={h.id} value={h.id}>
                {h.reit_name}
                {h.ticker_symbol ? ` (${h.ticker_symbol})` : ""}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Payment Date */}
      <div className="space-y-2">
        <Label htmlFor="dividend-date">Payment Date *</Label>
        <Input
          id="dividend-date"
          type="date"
          value={paymentDate}
          onChange={(e) => setPaymentDate(e.target.value)}
          required
          aria-required="true"
        />
      </div>

      {/* Amount per unit & Units */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="dividend-amount-per-unit">Amount per Unit *</Label>
          <Input
            id="dividend-amount-per-unit"
            type="number"
            step="0.0001"
            min="0.0001"
            value={amountPerUnit}
            onChange={(e) => setAmountPerUnit(e.target.value)}
            placeholder="e.g. 0.52"
            required
            aria-required="true"
            aria-label="Dividend amount per unit"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="dividend-units">Units Held at Payment *</Label>
          <Input
            id="dividend-units"
            type="number"
            step="0.001"
            min="0.001"
            value={unitsAtPayment}
            onChange={(e) => setUnitsAtPayment(e.target.value)}
            required
            aria-required="true"
            aria-label="Units held at time of payment"
          />
        </div>
      </div>

      {/* Currency */}
      <div className="space-y-2">
        <Label htmlFor="dividend-currency">Currency</Label>
        <Select value={currency} onValueChange={setCurrency}>
          <SelectTrigger id="dividend-currency" aria-label="Select currency">
            <SelectValue />
          </SelectTrigger>
          {isLoadingCurrencies && "Loading Currencies"}
          {!isLoadingCurrencies && !errorLoadingCurrencies ? (
            <SelectContent>
              {availableCurrencies.map((c) => (
                <SelectItem key={c} value={c}>
                  {c}
                </SelectItem>
              ))}
            </SelectContent>
          ) : (
            "Failed to load currencies"
          )}
        </Select>
      </div>

      {/* Computed total */}
      {totalAmount && (
        <div
          className="rounded-md bg-muted px-4 py-3 text-sm"
          role="status"
          aria-live="polite"
        >
          Total dividend payment:{" "}
          <span className="font-semibold">
            {currency} {totalAmount}
          </span>
        </div>
      )}

      {/* Dividend action toggle */}
      <div className="space-y-2">
        <Label>Dividend Action</Label>
        <div className="flex gap-2">
          <Button
            type="button"
            variant={dividendAction === "deposit_to_account" ? "default" : "outline"}
            onClick={() => setDividendAction("deposit_to_account")}
            aria-pressed={dividendAction === "deposit_to_account"}
          >
            Deposit to account
          </Button>
          <Button
            type="button"
            variant={dividendAction === "reinvest" ? "default" : "outline"}
            onClick={() => setDividendAction("reinvest")}
            aria-pressed={dividendAction === "reinvest"}
          >
            Reinvest
          </Button>
        </div>
      </div>

      {/* Deposit to account: target account select + ConversionPreview */}
      {dividendAction === "deposit_to_account" && (
        <div className="space-y-2">
          <Label htmlFor="dividend-target-account">Target Account *</Label>
          <Select value={targetAccountId} onValueChange={setTargetAccountId}>
            <SelectTrigger
              id="dividend-target-account"
              aria-label="Select target account"
            >
              <SelectValue placeholder="Select target account" />
            </SelectTrigger>
            <SelectContent>
              {disambiguatedAccounts.map((a) => (
                <SelectItem key={a.id} value={a.id}>
                  {a.displayName} ({a.currency})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {showConversionPreview && (
            <ConversionPreview
              amount={parseFloat(totalAmount!)}
              fromCurrency={currency}
              toCurrency={targetAccountCurrency}
              label="Estimated credit"
              getRate={getRate}
              isLoadingRates={isLoadingRates}
            />
          )}
        </div>
      )}

      {/* Reinvest: units to reinvest */}
      {dividendAction === "reinvest" && (
        <div className="space-y-2">
          <Label htmlFor="dividend-reinvested-units">Units to Reinvest *</Label>
          <Input
            id="dividend-reinvested-units"
            type="number"
            step="0.000001"
            min="0.000001"
            value={reinvestedUnits}
            onChange={(e) => setReinvestedUnits(e.target.value)}
            placeholder={
              totalAmount
                ? (
                    parseFloat(totalAmount) /
                    (selectedHolding?.current_price_per_unit || 1)
                  ).toFixed(6)
                : ""
            }
            aria-label="Units to reinvest"
          />
        </div>
      )}

      {/* Notes */}
      <div className="space-y-2">
        <Label htmlFor="dividend-notes">Notes</Label>
        <Textarea
          id="dividend-notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Optional notes"
          rows={2}
          aria-label="Optional notes"
        />
      </div>

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" disabled={isSubmitDisabled}>
          Record Payment
        </Button>
      </DialogFooter>
    </form>
  );
}
